/**
 * Team Status Panel
 *
 * Displays creature HP bars for both the current player's team
 * and their partner's team during an adventure.
 */

import React from 'react';

interface CreatureStatus {
  currentHp: number;
  maxHp: number;
  status: string;
  ownerId: string;
}

interface TeamStatusPanelProps {
  creatureStates: Record<string, CreatureStatus>;
  currentUserId: string;
}

export const TeamStatusPanel: React.FC<TeamStatusPanelProps> = ({
  creatureStates,
  currentUserId,
}) => {
  const creatures = Object.entries(creatureStates);
  const myCreatures = creatures.filter(([, c]) => c.ownerId === currentUserId);
  const partnerCreatures = creatures.filter(([, c]) => c.ownerId !== currentUserId);

  const renderCreature = (id: string, creature: CreatureStatus, color: string) => {
    const hpPercent = creature.maxHp > 0 ? (creature.currentHp / creature.maxHp) * 100 : 0;
    const isDead = creature.status === 'dead';

    return (
      <div
        key={id}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          marginBottom: '0.25rem',
          opacity: isDead ? 0.4 : 1,
        }}
      >
        <span style={{ color, fontSize: '0.7rem', width: '8px' }}>{'\u25CF'}</span>
        <span style={{
          fontSize: '0.8rem',
          color: '#fff',
          width: '80px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {id.slice(0, 10)}
        </span>
        <div style={{
          flex: 1,
          height: '8px',
          background: '#333',
          borderRadius: '4px',
          overflow: 'hidden',
        }}>
          <div style={{
            width: `${hpPercent}%`,
            height: '100%',
            borderRadius: '4px',
            background: isDead
              ? '#666'
              : hpPercent > 50
                ? '#4ade80'
                : hpPercent > 25
                  ? '#fbbf24'
                  : '#ef4444',
            transition: 'width 0.3s',
          }} />
        </div>
        <span style={{
          fontSize: '0.7rem',
          color: '#aaa',
          width: '45px',
          textAlign: 'right',
        }}>
          {isDead ? 'DEAD' : `${Math.round(hpPercent)}%`}
        </span>
      </div>
    );
  };

  return (
    <div style={{
      background: 'rgba(0, 0, 0, 0.4)',
      borderRadius: '8px',
      padding: '0.75rem',
    }}>
      {myCreatures.length > 0 && (
        <div style={{ marginBottom: '0.5rem' }}>
          <div style={{
            fontSize: '0.75rem',
            color: '#FFD700',
            marginBottom: '0.25rem',
            fontWeight: 'bold',
          }}>
            Your Team
          </div>
          {myCreatures.map(([id, c]) => renderCreature(id, c, '#FFD700'))}
        </div>
      )}

      {partnerCreatures.length > 0 && (
        <div>
          <div style={{
            fontSize: '0.75rem',
            color: '#B87333',
            marginBottom: '0.25rem',
            fontWeight: 'bold',
          }}>
            Partner&apos;s Team
          </div>
          {partnerCreatures.map(([id, c]) => renderCreature(id, c, '#B87333'))}
        </div>
      )}

      {creatures.length === 0 && (
        <div style={{
          color: '#888',
          fontSize: '0.85rem',
          textAlign: 'center',
        }}>
          No creatures assigned
        </div>
      )}
    </div>
  );
};
